import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { Lesson, PlanLessonsResponse } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { Crumbs } from "../shell/Crumbs.tsx";
import { LessonWho } from "../lessons/LessonWho.tsx";
import { lessonPeopleCount } from "./plan-lesson-people.ts";
import { meetingCountText, studentCountText } from "../../lib/plurals.ts";
import { agoLabel } from "../../lib/when.ts";
import { Empty } from "../../lib/Empty.tsx";
import { LoadingRows } from "../../lib/loading.tsx";

/**
 * Уроки, собранные по одной заготовке.
 *
 * Экран закрывает пробел, названный в карте навигации дизайнера: заготовка не
 * знала о своих уроках вовсе, и «по какому материалу я это вела» приходилось
 * восстанавливать по названиям в общем списке.
 *
 * Три решения:
 *
 * 1. **Фильтрует база, а не браузер.** Ходим за отдельной ручкой
 *    `/v1/plans/:planId/lessons`, которую обслуживает частичный индекс
 *    `lesson_plan_idx`. Тянуть весь список уроков и отбирать свои на клиенте
 *    нельзя: у преподавателя с историей их тысячи
 * 2. **Заготовка приезжает тем же ответом.** Хлебным крошкам нужно её название,
 *    и второй круг по сети ради строки заголовка на канале 128 кбит/с заметен
 * 3. **Строки с фиксированными слотами, а не карточки.** Таблицу дизайнера
 *    откладывала ровно одна её колонка, доля верных ответов, которой у нас всё
 *    ещё нет. Решение пересмотрено владельцем 18.08.2026: таблица делается
 *    сейчас и без этой колонки. Первой смысловой колонкой стоит ученик, а не
 *    название урока: экран отвечает на вопрос «с кем я это проходила», а
 *    названия у уроков одной заготовки как раз похожи друг на друга
 *
 * Доли верных ответов здесь нет намеренно: она считается по `exercise_attempt`
 * и это отдельная работа.
 */

export function PlanLessonsPage() {
  const { planId = "" } = useParams();
  const [data, setData] = useState<PlanLessonsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await api.listPlanLessons(planId);
        if (alive) setData(res);
      } catch (e) {
        if (!alive) return;
        // Чужая и удалённая заготовка одинаково «не найдена»: сервер отвечает
        // 404 в обоих случаях намеренно, и объяснять разницу здесь нечем
        setErr(
          e instanceof api.ApiError && e.status === 404
            ? "Заготовка не найдена"
            : e instanceof Error
              ? e.message
              : String(e),
        );
      }
    })();
    return () => {
      alive = false;
    };
  }, [planId]);

  /*
    Ступень колонки стоит и у отказа, и у загрузки: иначе экран сначала рисуется
    одной ширины, а после ответа прыгает в другую, и прыгает он ровно там, куда
    человек уже перевёл взгляд
  */
  if (err !== null) {
    return (
      <main className="home home--list">
        <header className="head">
          <Crumbs trail={[{ label: "Заготовки", to: "/plans" }]} here="Уроки" />
          <div className="head__line">
            <h1 className="head__h">Уроки по заготовке</h1>
          </div>
        </header>
        <p className="err">{err}</p>
        <p>
          <Link className="btn" to="/plans">
            К списку заготовок
          </Link>
        </p>
      </main>
    );
  }

  if (data === null) {
    return (
      <main className="home home--list">
        <header className="head">
          <Crumbs trail={[{ label: "Заготовки", to: "/plans" }]} here="Уроки" />
          <div className="head__line">
            <h1 className="head__h">Уроки по заготовке</h1>
          </div>
        </header>
        <section className="card">
          <LoadingRows list="list--plan-lessons" cells={5} />
        </section>
      </main>
    );
  }

  const lessons = data.lessons;
  /** Люди, а не строки: у занятия группы за одной строкой их целый класс. */
  const students = lessonPeopleCount(lessons);
  // Одно «сейчас» на всю отрисовку: с `Date.now()` в каждой строке два урока
  // одной минуты могут получить разные ярлыки
  const now = Date.now();

  return (
    <main className="home home--list">
      {/*
        Главного действия у экрана нет: урок по заготовке создают в самой
        заготовке, и крошка ведёт туда же. Место справа стоит пустым.
      */}
      <header className="head">
        {/*
          Хлебные крошки, а не одна кнопка «назад»: с этого экрана ведут две
          разные дороги, к списку заготовок и в саму заготовку, и обе нужны.
        */}
        <Crumbs
          trail={[
            { label: "Заготовки", to: "/plans" },
            { label: data.plan.title, to: `/plans/${data.plan.id}` },
          ]}
          here="Уроки"
        />
        <div className="head__line">
          <h1 className="head__h">Уроки по этой заготовке</h1>
          {lessons.length > 0 && (
            <span className="head__n">{meetingCountText(lessons.length)}</span>
          )}
        </div>
        {/* Людей считаем отдельной строкой, а не вторым числом в строке
            заголовка: счётчик у заголовка один, и отвечает он на вопрос
            «сколько тут строк» */}
        {lessons.length > 0 && <div className="head__sub">{studentCountText(students)}</div>}
      </header>

      <section className="card">
        {lessons.length === 0 ? (
          /*
            Единственное действие пустого экрана, и оно же ответ на вопрос, с
            которым сюда пришли: уроков нет, создают их в заготовке. Когда уроки
            есть, акцента на экране нет вовсе, потому что строк много и выделять
            одну из них не за что (правило 4).

            ⚠️ Строки «Кнопка „Создать урок“ стоит в шапке открытой заготовки»
            здесь больше нет: кнопка плашки ведёт ровно туда, и объяснять
            словами дорогу к тому, что стоит под самой фразой, незачем.
          */
          <Empty
            icon="board"
            title="Уроков по этой заготовке пока нет"
            action={{ label: "Открыть заготовку", to: `/plans/${data.plan.id}` }}
          />
        ) : (
          <>
            <p className="muted">
              Каждый урок получил свою копию слайдов. Правки заготовки меняют только те уроки,
              которые ещё не открывали.
            </p>
            <ul className="list list--plan-lessons">
              <li className="list__head" aria-hidden="true">
                <span>Дата</span>
                <span>Ученик</span>
                <span>Урок</span>
                <span>Состояние</span>
                <span>Действия</span>
              </li>
              {lessons.map((l) => (
                <PlanLessonRow key={l.id} lesson={l} now={now} />
              ))}
            </ul>
          </>
        )}
      </section>
    </main>
  );
}

function PlanLessonRow({ lesson, now }: { lesson: Lesson; now: number }) {
  const ended = lesson.status === "ended";
  const when = agoLabel(lesson.createdAt, now);
  const state = lessonState(lesson);

  return (
    <li className="row" data-dim={ended}>
      <span
        className="cell row__when"
        data-label="Дата"
        data-tone={when.tone}
        title={when.title}
      >
        {when.text}
      </span>

      {/*
        Ученик главной ячейкой, а название урока обычной: по названию уроки
        одной заготовки не различаются, а по имени различаются всегда.

        ⚠️ Общая с архивом и списком уроков колонка (`LessonWho`), а не своя: у
        свёрнутой строки занятия группы ученика нет вовсе, и своя редакция
        подписывала её серым «без ученика» вместо названия группы, то есть
        отнимала у строки ровно ту различимость, ради которой ячейка и главная.
      */}
      <LessonWho lesson={lesson} main />

      <span className="cell" data-label="Урок">
        {lesson.title}
      </span>

      <span className="cell" data-label="Состояние">
        <span className="pill" data-tone={state.tone}>
          {state.text}
        </span>
      </span>

      <span className="cell row__act">
        <PlanLessonAction lesson={lesson} ended={ended} />
      </span>
    </li>
  );
}

/**
 * Одно действие на строку.
 *
 * Пока урок не завершён, это вход, и вход выигрывает у архива даже при готовом
 * снимке: снимок снимается раз в пять минут и у идущего занятия он есть, так
 * что правило «архив, когда снимок есть» в лоб закрыло бы вход туда, где прямо
 * сейчас урок. Архив только при готовом снимке, ровно как в кабинете: кнопка на
 * урок без снимка вела бы на экран «архив готовится», который для урока, где
 * никто ничего не нарисовал, не закончится никогда.
 */
function PlanLessonAction({ lesson, ended }: { lesson: Lesson; ended: boolean }) {
  if (!ended) {
    return (
      <Link className="btn btn--sm" to={`/lesson/${lesson.id}`}>
        Войти
      </Link>
    );
  }
  if (lesson.hasBoardSnapshot === true) {
    return (
      <Link className="btn btn--sm" to={`/archive/${lesson.id}`}>
        Архив
      </Link>
    );
  }
  // Занятие прошло, но на доске ничего не сохранилось: архива у него не будет
  // никогда, а входить в завершённый урок мы не предлагаем нигде. Слот при этом
  // занят словами, а не пустотой, иначе строка снова поедет
  return <span className="row__void">открывать нечего</span>;
}

/**
 * Состояние урока одним чипом.
 *
 * Считается по двум полям, которые уже приехали в списке, и это осознанный
 * потолок: полное состояние относительно заготовки (совпадает, отошёл) требует
 * снимка доски на каждый урок, то есть сотен килобайт jsonb на строку. На
 * карточке урока в кабинете оно поэтому подгружается по кнопке, а здесь его нет
 * вовсе: экран отвечает на вопрос «где я это вела», а не «что там на доске».
 *
 * Тон ровно у одного состояния, у идущего урока: цветное перестаёт выделяться
 * ровно тогда, когда цветного становится много, а идущее занятие это
 * единственное, куда с этого экрана заходят сейчас.
 */
function lessonState(lesson: Lesson): { text: string; tone: "ok" | undefined } {
  if (lesson.status === "ended") return { text: "завершён", tone: undefined };
  // Материал заготовки урок берёт при первом открытии доски. Пока не взял,
  // правки заготовки доезжают до него сами, и это стоит сказать
  if (lesson.planId != null && lesson.planMaterializedAt == null) {
    return { text: "материал не взят", tone: undefined };
  }
  if (lesson.status === "live") return { text: "идёт", tone: "ok" };
  return { text: "создан", tone: undefined };
}
